<template>
  <div ref="root" class="pl-account">
    <PlIconButton
      label="Ustawienia"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >
      <PlIcon name="settings" :size="18" />
    </PlIconButton>
    <button
      type="button"
      class="pl-account__avatar"
      :aria-label="`Profil: ${email || 'konto'}`"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >{{ initials }}</button>

    <div v-if="open" class="pl-account__menu" role="menu">
      <p class="pl-account__email">{{ email }}</p>
      <PlThemeSwitch class="pl-account__theme" />
      <template v-if="isAdmin">
        <p class="pl-account__group">Administracja</p>
        <router-link
          v-for="item in ADMIN_LINKS"
          :key="item.to"
          :to="item.to"
          class="pl-account__item"
          role="menuitem"
          @click="open = false"
        >{{ item.label }}</router-link>
      </template>
      <button type="button" class="pl-account__item" role="menuitem" @click="logout">Wyloguj się</button>
      <button type="button" class="pl-account__item pl-account__item--danger" role="menuitem" @click="deleteAllData">
        Usuń wszystkie dane
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import { userInitials } from '@/utils/format'
import PlIcon from './PlIcon.vue'
import PlIconButton from './PlIconButton.vue'
import PlThemeSwitch from './PlThemeSwitch.vue'

const authStore = useAuthStore()
const router = useRouter()
const root = ref(null)
const open = ref(false)

const email = computed(() => authStore.user?.email || '')
const initials = computed(() => userInitials(email.value))
const isAdmin = computed(() => authStore.isAdmin)

const ADMIN_LINKS = [
  { to: '/admin/challenges', label: 'Wyzwania' },
  { to: '/admin/users', label: 'Użytkownicy' },
  { to: '/admin/peaks', label: 'Szczyty' },
  { to: '/admin/countries', label: 'Kraje' },
  { to: '/admin/activities', label: 'Aktywności' },
  { to: '/admin/map', label: 'Mapa zdobyć' }
]

const toggle = () => {
  open.value = !open.value
}

const logout = () => {
  open.value = false
  authStore.logout()
  router.push('/login')
}

const deleteAllData = async () => {
  open.value = false
  if (!confirm('Na pewno usunąć wszystkie Twoje dane? Tej operacji nie można cofnąć.')) return
  try {
    await api.delete('/api/user')
    alert('Dane zostały usunięte. Nastąpi wylogowanie.')
    authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Error deleting data:', error)
    alert('Nie udało się usunąć danych. Spróbuj ponownie.')
  }
}

const onDocumentClick = event => {
  if (open.value && root.value && !root.value.contains(event.target)) open.value = false
}

const onKeydown = event => {
  if (event.key === 'Escape') open.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.pl-account {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
}

.pl-account__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid var(--pl-color-border-strong);
  border-radius: var(--pl-radius-pill);
  background: var(--pl-color-bg-raised);
  color: var(--pl-color-text-primary);
  font: var(--pl-type-avatar);
  cursor: pointer;
}

.pl-account__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 260px;
  padding: 6px;
  border: 1px solid var(--pl-color-border-strong);
  border-radius: var(--pl-radius-control);
  background: var(--pl-color-bg-card);
  box-shadow: var(--pl-shadow-device);
}

.pl-account__email {
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--pl-color-border-divider);
  margin-bottom: 4px;
  overflow: hidden;
  font: var(--pl-type-caption);
  color: var(--pl-color-text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pl-account__theme {
  margin-bottom: 4px;
  border-bottom: 1px solid var(--pl-color-border-divider);
}

.pl-account__group {
  padding: 8px 12px 2px;
  font: var(--pl-type-overline);
  color: var(--pl-color-text-faint);
}

.pl-account__item {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 12px;
  border: 0;
  border-radius: var(--pl-radius-tile);
  background: none;
  color: var(--pl-color-text-primary);
  font: var(--pl-type-small-strong);
  text-align: left;
  cursor: pointer;
}

.pl-account__item:hover {
  background: var(--pl-color-bg-raised);
}

.pl-account__item--danger {
  color: var(--pl-color-status-error);
}
</style>
